'use client';

import React from 'react';
import {
  ArrowDownToDotIcon,
  BookOpen01Icon,
  Bug01Icon,
  Target01Icon,
  Globe02Icon,
  Radar01Icon,
  SentIcon,
  TextSelectIcon,
} from '@terra/ui-icons';
import type {
  MentionEntity,
  HttpMentionData,
  InteractMentionData,
  FindingMentionData,
  ObjectContextMentionData,
  ExternalAssetMentionData,
} from '@terra/shared-dto-chat-types';
import { HttpMentionCard } from '../components/HttpMentionCard';
import { InteractMentionCard } from '../components/InteractMentionCard';
import { FindingMentionCard } from '../components/FindingMentionCard';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/styled/text';

type MentionCardRenderer = (props: {
  mention: MentionEntity;
  compact?: boolean;
}) => React.ReactNode;
type MentionConfig = {
  icon: React.ComponentType<{
    size?: number;
  }>;
  label: string;
  extraDataKey: string;
  renderCard: MentionCardRenderer;
};
const MENTION_CONFIG: Partial<
  Record<MentionEntity['entityType'], MentionConfig>
> = {
  HttpRequest: {
    icon: SentIcon,
    label: 'HTTP Request',
    extraDataKey: 'httpRequest',
    renderCard: ({ mention, compact }) => {
      const httpData = mention.extraData?.httpRequest as
        | HttpMentionData
        | undefined;

      if (!httpData) return null;

      return (
        <HttpMentionCard data={httpData} type="request" compact={compact} />
      );
    },
  },
  HttpResponse: {
    icon: ArrowDownToDotIcon,
    label: 'HTTP Response',
    extraDataKey: 'httpRequest',
    renderCard: ({ mention, compact }) => {
      const httpData = mention.extraData?.httpRequest as
        | HttpMentionData
        | undefined;

      if (!httpData) return null;

      return (
        <HttpMentionCard data={httpData} type="response" compact={compact} />
      );
    },
  },
  Interact: {
    icon: Globe02Icon,
    label: 'Interact',
    extraDataKey: 'interact',
    renderCard: ({ mention, compact }) => {
      const interactData = mention.extraData?.interact as
        | InteractMentionData
        | undefined;

      if (!interactData) return null;

      return <InteractMentionCard data={interactData} compact={compact} />;
    },
  },
  Finding: {
    icon: Bug01Icon,
    label: 'Finding',
    extraDataKey: 'finding',
    renderCard: ({ mention, compact }) => {
      const findingData = mention.extraData?.finding as
        | FindingMentionData
        | undefined;

      if (!findingData) return null;

      return <FindingMentionCard data={findingData} compact={compact} />;
    },
  },
  TestingInstruction: {
    icon: TextSelectIcon,
    label: 'Text context',
    extraDataKey: 'testingInstruction',
    renderCard: ({ mention, compact }) => {
      const data = mention.extraData?.testingInstruction as
        | {
            title?: string;
            field?: string;
            context?: string;
            excerpt?: string;
          }
        | undefined;

      if (!data) return null;
      const excerpt = data.excerpt ?? mention.label;

      return (
        <Box
          borderRadius="md"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.default"
          bg="bg.subtle"
          px={compact ? 2.5 : 3}
          py={compact ? 2 : 2.5}
        >
          <VStack alignItems="stretch" gap={1}>
            <HStack gap={1.5} alignItems="center">
              <TextSelectIcon size={14} />
              <Text size="xs" fontWeight="semibold" color="fg.default">
                {data.title ?? mention.label}
              </Text>
            </HStack>
            <Text size="xs" color="fg.muted">
              {[data.context, data.field].filter(Boolean).join(' · ')}
            </Text>
            <Text
              size="xs"
              color="fg.default"
              lineClamp={compact ? 3 : undefined}
              whiteSpace="pre-wrap"
            >
              {excerpt}
            </Text>
          </VStack>
        </Box>
      );
    },
  },
  Skill: {
    icon: BookOpen01Icon,
    label: 'Skill',
    extraDataKey: 'skill',
    renderCard: () => null,
  },
  ObjectContext: {
    icon: Target01Icon,
    label: 'Marked context',
    extraDataKey: 'objectContext',
    renderCard: ({ mention, compact }) => {
      const data = mention.extraData?.objectContext as
        | ObjectContextMentionData
        | undefined;

      if (!data) return null;
      if (data.objectKind === 'SourceCode') {
        return null;
      }

      const fieldLabels =
        data.fields?.map((field) => field.label).join(', ') ?? '';
      const selectionMeta = data.selection
        ? [
            data.selection.field,
            data.selection.lineStart !== undefined &&
            data.selection.lineEnd !== undefined
              ? `lines ${data.selection.lineStart}-${data.selection.lineEnd}`
              : undefined,
          ]
            .filter(Boolean)
            .join(' · ')
        : '';
      const excerpt =
        data.selection?.excerpt ?? data.fields?.[0]?.value ?? data.objectLabel;

      return (
        <Box
          borderRadius="md"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.default"
          bg="bg.subtle"
          px={compact ? 2.5 : 3}
          py={compact ? 2 : 2.5}
        >
          <VStack alignItems="stretch" gap={1}>
            <HStack gap={1.5} alignItems="center">
              <Target01Icon size={14} />
              <Text size="xs" fontWeight="semibold" color="fg.default">
                {data.objectKind}: {data.objectLabel}
              </Text>
            </HStack>
            {fieldLabels || selectionMeta ? (
              <Text size="xs" color="fg.muted">
                {[fieldLabels, selectionMeta].filter(Boolean).join(' · ')}
              </Text>
            ) : null}
            <Text
              size="xs"
              color="fg.default"
              lineClamp={compact ? 3 : undefined}
              whiteSpace="pre-wrap"
            >
              {excerpt}
            </Text>
          </VStack>
        </Box>
      );
    },
  },
  ExternalAsset: {
    icon: Radar01Icon,
    label: 'External asset',
    extraDataKey: 'externalAsset',
    renderCard: ({ mention, compact }) => {
      const data = mention.extraData?.externalAsset as
        | ExternalAssetMentionData
        | undefined;

      if (!data) return null;

      return (
        <Box
          borderRadius="md"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.default"
          bg="bg.subtle"
          px={compact ? 2.5 : 3}
          py={compact ? 2 : 2.5}
        >
          <VStack alignItems="stretch" gap={1}>
            <HStack gap={1.5} alignItems="center">
              <Radar01Icon size={14} />
              <Text size="xs" fontWeight="semibold" color="fg.default">
                {data.assetKind}: {data.label}
              </Text>
            </HStack>
            {data.subtitle ? (
              <Text size="xs" color="fg.muted">
                {data.subtitle}
              </Text>
            ) : null}
          </VStack>
        </Box>
      );
    },
  },
};
const RICH_CARD_ENTITY_TYPES = new Set(Object.keys(MENTION_CONFIG));

export function getMentionConfig(
  entityType: MentionEntity['entityType'],
): MentionConfig | undefined {
  return MENTION_CONFIG[entityType];
}

export function hasRichCardRenderer(
  entityType: MentionEntity['entityType'],
): boolean {
  return RICH_CARD_ENTITY_TYPES.has(entityType);
}

export function renderMentionCard(
  mention: MentionEntity,
  compact = true,
): React.ReactNode {
  const config = getMentionConfig(mention.entityType);

  if (!config) return null;

  return config.renderCard({ mention, compact });
}

export function separateMentionsByType(mentions: MentionEntity[]): {
  richCardMentions: MentionEntity[];
  badgeMentions: MentionEntity[];
} {
  const richCardMentions: MentionEntity[] = [];
  const badgeMentions: MentionEntity[] = [];

  for (const mention of mentions) {
    if (hasRichCardRenderer(mention.entityType)) {
      richCardMentions.push(mention);
    } else {
      badgeMentions.push(mention);
    }
  }

  return { richCardMentions, badgeMentions };
}

export function extractRawTextFromMention(
  mention: MentionEntity,
): string | undefined {
  const config = getMentionConfig(mention.entityType);

  if (!config) return undefined;
  const data = mention.extraData?.[config.extraDataKey] as
    | {
        rawText?: string;
      }
    | undefined;

  return data?.rawText;
}
